import Microgame from '../engine/Microgame.js';

export default class DartGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Fléchettes !`, en: `Darts!`, es: `¡Dardos!` },
            controls: { fr: `Glisse vers l'avant au bon moment pour lancer la fléchette`, en: `Swipe forward at the right time to throw`, es: `Desliza hacia adelante en el momento ideal para lanzar` },
            objective: { fr: `Touche la cible au centre`, en: `Hit the center of the target`, es: `Golpea el centro de la diana` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 84, toX: 50, toY: 22, duration: 300 },
                { type: 'wait', duration: 720 },
            ],
            loopDuration: 2400
        };
    }
    constructor(engine) {
        super(engine);
        this.canvas = null;
        this.ctx = null;

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.4;
        this.duration = 1860 * (speedMult / adj);
        this.speedMult = speedMult;

        this.thrown = false;
        this._isAnimating = false;
        this._frozenTimerValue = 0;

        // Swipe tracking
        this.swipeStartX = 0;
        this.swipeStartY = 0;
        this.swipeStartTime = 0;
        this.isSwiping = false;

        // Dart state
        this.dartX = 0;
        this.dartY = 0;
        this.dartVX = 0;
        this.dartVY = 0;
        this.dartAngle = -Math.PI / 2;
        this.dartLanded = false;
    }

    setup() {
        this.engine.ui.instruction.textContent = "THROW!";

        const W = this.container.clientWidth || 400;
        const H = this.container.clientHeight || 700;

        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.width = W;
        this.canvas.height = H;
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Board: always centered horizontally
        // Height depends on difficulty: closer at x1, further (higher) at x4
        // Size shrinks with difficulty
        const baseSize = Math.min(W, H) * 0.22;
        const sizeFactor = 1 - (this.speedMult - 1) * 0.20; // 1.0 at x1, 0.4 at x4
        this.boardRadius = baseSize * Math.max(sizeFactor, 0.40);

        this.boardCX = W / 2;
        // Higher on screen = further away. x1: 45%, x4: 18%
        const heightPct = 0.45 - (this.speedMult - 1) * 0.09;
        this.boardCY = H * Math.max(heightPct, 0.15);

        // Bullseye
        this.bullseyeRadius = this.boardRadius * 0.22;

        // Dart initial position (bottom center)
        this.dartX = W / 2;
        this.dartY = H * 0.85;
        this.dartStartY = this.dartY;

        // Input
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
        this.engine.input.element.addEventListener('pointercancel', this.onUp);

        this.lastTime = null;
        this.animate();
    }

    getCanvasCoords(e) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (this.canvas.width / rect.width),
            y: (e.clientY - rect.top) * (this.canvas.height / rect.height),
        };
    }

    onDown(e) {
        if (this.thrown || this.won || this.lost) return;
        this.isSwiping = true;
        const pos = this.getCanvasCoords(e);
        this.swipeStartX = pos.x;
        this.swipeStartY = pos.y;
        this.swipeStartTime = performance.now();
    }

    onMove() { }

    onUp(e) {
        if (!this.isSwiping || this.thrown || this.won || this.lost) return;
        this.isSwiping = false;

        const pos = this.getCanvasCoords(e);
        const elapsed = Math.max((performance.now() - this.swipeStartTime) / 1000, 0.03);

        const dx = pos.x - this.swipeStartX;
        const dy = pos.y - this.swipeStartY;
        const dist = Math.hypot(dx, dy);

        // Require upward swipe with minimum distance
        if (dist < 25 || dy > -15) return;

        this.thrown = true;
        this._isAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        // Depth simulation: Dart starts at Z=100 and flies to Z=0.
        this.dartZ = 100;
        this.dartVZ = -3.0; // It will take ~33 frames to reach the board

        const absDy = Math.abs(dy);
        
        // Power based on swipe vertical distance, capping the max distance.
        // This softens the throw and makes it much more precise and predictable.
        // A small swipe (~60px) works for close targets, a large swipe (~280px) for far targets.
        const distancePower = Math.min(absDy, 350) / 350; // 0.0 to 1.0
        
        // Base force scales gently
        const baseForce = 8.5 + (distancePower * 10);

        // Vectorize the force based on the exact drag angle for precise aiming
        this.dartVX = (dx / dist) * baseForce * 0.75; // 0.75 to narrow horizontal spread slightly
        this.dartVY = (dy / dist) * baseForce;

        this.dartAngle = Math.atan2(this.dartVY, this.dartVX);
    }

    animate() {
        if (!this.active) return;

        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }

        const now = performance.now();
        if (!this.lastTime) this.lastTime = now;
        const dtMs = Math.min(now - this.lastTime, 50);
        const dt = dtMs / 16.67;
        this.lastTime = now;

        if (this.thrown && !this.dartLanded) {
            this.dartX += this.dartVX * dt;
            this.dartY += this.dartVY * dt;
            this.dartZ += this.dartVZ * dt;

            // Gravity
            this.dartVY += 0.12 * dt;

            // Update angle
            this.dartAngle = Math.atan2(this.dartVY, this.dartVX);

            // Did it hit the wall (Z <= 0)?
            if (this.dartZ <= 0) {
                this.dartZ = 0;
                this.dartLanded = true;
                
                // Calculate the exact tip position of the dart for pixel-perfect logic
                // The visual tip is at Y=-22 locally. When Z=0, scale is 0.5.
                const tipOffset = 11; // 22 * 0.5
                const tipX = this.dartX + tipOffset * Math.cos(this.dartAngle);
                const tipY = this.dartY + tipOffset * Math.sin(this.dartAngle);

                // Use the tip's exact position to check collision
                const distToBoard = Math.hypot(tipX - this.boardCX, tipY - this.boardCY);

                if (distToBoard <= this.boardRadius) {
                    // Bonus score calculation
                    let bonus = 0;
                    if (distToBoard <= this.bullseyeRadius) {
                        bonus = 50;
                    } else if (distToBoard <= this.boardRadius * 0.38) {
                        bonus = 20;
                    } else if (distToBoard <= this.boardRadius * 0.58) {
                        bonus = 10;
                    } else if (distToBoard <= this.boardRadius * 0.78) {
                        bonus = 5;
                    }
                    
                    if (bonus > 0 && this.engine.score !== undefined) {
                        this.engine.score += bonus;
                    }
                    this.earnedBonus = bonus;

                    setTimeout(() => this.win(), 250);
                } else {
                    setTimeout(() => this.lose(), 250);
                }
            }

            // Off-screen sides (missed)
            const W = this.canvas.width;
            const H = this.canvas.height;
            if ((this.dartX < -60 || this.dartX > W + 60) && !this.dartLanded) {
                this.dartLanded = true;
                setTimeout(() => this.lose(), 150);
            }
        }

        this.draw();
        requestAnimationFrame(() => this.animate());
    }


    draw() {
        const ctx = this.ctx;
        const W = this.canvas.width;
        const H = this.canvas.height;
        ctx.clearRect(0, 0, W, H);

        const cx = this.boardCX;
        const cy = this.boardCY;
        const R = this.boardRadius;

        // === DARTBOARD ===
        // Shadow
        ctx.beginPath();
        ctx.arc(cx + 3, cy + 4, R + 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.fill();

        // Outer ring (primary) with glow
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.fillStyle = this.primaryColor;
        ctx.shadowColor = this.primaryColor;
        ctx.shadowBlur = 15;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Ring 2 (dark shadow)
        ctx.beginPath();
        ctx.arc(cx, cy, R * 0.78, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.35)'; // Ombre
        ctx.fill();

        // Ring 3 (primary)
        ctx.beginPath();
        ctx.arc(cx, cy, R * 0.58, 0, Math.PI * 2);
        ctx.fillStyle = this.primaryColor;
        ctx.fill();

        // Ring 4 (dark shadow)
        ctx.beginPath();
        ctx.arc(cx, cy, R * 0.38, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.4)'; // Ombre plus marquée au centre
        ctx.fill();

        // Bullseye (primary with bright glow)
        ctx.beginPath();
        ctx.arc(cx, cy, this.bullseyeRadius, 0, Math.PI * 2);
        ctx.fillStyle = this.primaryColor;
        ctx.shadowColor = '#fff';
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Center dot
        ctx.beginPath();
        ctx.arc(cx, cy, 4, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();

        // Wire cross
        ctx.strokeStyle = 'rgba(255,255,255,0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx - R, cy); ctx.lineTo(cx + R, cy);
        ctx.moveTo(cx, cy - R); ctx.lineTo(cx, cy + R);
        ctx.stroke();

        // Border
        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.stroke();

        // === DART ===
        this.drawDart(ctx, this.dartX, this.dartY, this.dartAngle);

        // === SWIPE HINT OR BONUS ===
        if (!this.thrown) {
            ctx.fillStyle = `${this.secondaryColor}77`;
            ctx.font = 'bold 14px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('↑ SWIPE UP ↑', W / 2, H * 0.95);
        } else if (this.dartLanded && this.earnedBonus !== undefined) {
            ctx.fillStyle = this.earnedBonus > 0 ? this.primaryColor : '#fff';
            ctx.font = '900 28px sans-serif';
            ctx.textAlign = 'center';
            const textY = cy + R + 45;
            if (this.earnedBonus > 0) {
                ctx.fillText(`+${this.earnedBonus}`, cx, textY);
            } else {
                ctx.fillText(`HIT`, cx, textY);
            }
        }
    }

    drawDart(ctx, x, y, angle) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle + Math.PI / 2);
        
        // Scale based on depth (Z=100 is scale 1.0, Z=0 is scale 0.5)
        const scale = 0.5 + (this.dartZ / 100) * 0.5;
        ctx.scale(scale, scale);

        // Shadow
        ctx.fillStyle = 'rgba(0,0,0,0.15)';
        ctx.beginPath();
        ctx.moveTo(2, -18);
        ctx.lineTo(-2, 17);
        ctx.lineTo(6, 17);
        ctx.closePath();
        ctx.fill();

        // Néon effect
        ctx.shadowColor = this.secondaryColor;
        ctx.shadowBlur = 15;

        // Body (Secondary)
        ctx.fillStyle = this.secondaryColor;
        ctx.beginPath();
        ctx.moveTo(0, -22);
        ctx.lineTo(-5, 16);
        ctx.lineTo(5, 16);
        ctx.closePath();
        ctx.fill();

        // Tip (White)
        ctx.fillStyle = '#fff';
        ctx.shadowBlur = 5; // Moins de néon pour la pointe
        ctx.beginPath();
        ctx.moveTo(0, -22);
        ctx.lineTo(-2, -14);
        ctx.lineTo(2, -14);
        ctx.closePath();
        ctx.fill();

        // Restore néon fort pour les plumes
        ctx.shadowBlur = 15;

        // Feathers (Secondary)
        ctx.fillStyle = this.secondaryColor;
        ctx.beginPath();
        ctx.moveTo(-4, 10);
        ctx.lineTo(-14, 24);
        ctx.lineTo(-3, 16);
        ctx.closePath();
        ctx.fill();

        ctx.beginPath();
        ctx.moveTo(4, 10);
        ctx.lineTo(14, 24);
        ctx.lineTo(3, 16);
        ctx.closePath();
        ctx.fill();

        // Clean
        ctx.shadowBlur = 0;
        ctx.restore();
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
        this.engine.input.element.removeEventListener('pointercancel', this.onUp);
    }
}
